import * as React from "react";
import { Redirect } from "react-router-dom";
import { toast, ToastOptions } from "react-toastify";

import orderBy from "lodash/orderBy";

import { ClientDefaultContext, ClientDefaultProvider } from "@app/admin/contexts/ClientDefaultContext";
import { NotFound } from "@app/shared/ErrorPages";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ClientService } from "@app/client/clientService";

const ClientDefaultRolesWithContext: React.FC<React.PropsWithChildren<unknown>> = () => {
  const clientDefaultContext = React.useContext(ClientDefaultContext);
  const user = React.useContext(UserContext);

  const [redirect, setRedirect] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [initialDefaults, setInitialDefaults] = React.useState(clientDefaultContext.defaultRoles.map((r) => r.name));

  const clientService = new ClientService();

  const selectedOptions = React.useMemo(() => clientDefaultContext.defaultRoles.map((r) => r.name), [clientDefaultContext.defaultRoles]);

  const roleOptions = React.useMemo(() => {
    const roleOptions: { [key: string]: MultiselectItem } = {};
    if (clientDefaultContext.availableRoles) {
      clientDefaultContext.availableRoles.forEach((r) => Object.assign(roleOptions,
        {
          [r.name]: {
            currentState: selectedOptions.find((so) => so === r.name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name,
          }
        }));
    }
    return roleOptions;
  }, [clientDefaultContext.availableRoles, selectedOptions]);

  const updateRoles = (response: MultiselectOnChangeResponse) => {
    if (loading) return;
    const selectedRoles = response.checkedKeys;
    const newRoles = selectedRoles.map((role) => {
      const matchedRoleModel = clientDefaultContext.availableRoles.find((r) => r.name === role);
      return matchedRoleModel;
    }).filter((r) => !!r);
    if (!(clientDefaultContext.defaultRoles.every((r) => newRoles.includes(r)) && newRoles.every((r) => clientDefaultContext.defaultRoles.includes(r)))) {
      clientDefaultContext.setDefaultRoles(orderBy(newRoles, (nr) => nr.name, "asc"));
    }
  }

  const updateRequired = React.useMemo(() => !(clientDefaultContext.defaultRoles.every((r) => initialDefaults.includes(r.name)) && initialDefaults.every((r) => clientDefaultContext.defaultRoles.map(r => r.name).includes(r))), [clientDefaultContext.defaultRoles, initialDefaults]);

  React.useEffect(() => {
    setLoading(false);
  }, []);

  React.useEffect(() => {
    if (initialDefaults.length == 0 && clientDefaultContext.defaultRoles.length > 0) {
      setInitialDefaults(clientDefaultContext.defaultRoles.map((r) => r.name));
    }
  }, [clientDefaultContext.defaultRoles])

  const handleCancel = () => {
    setRedirect("../client-administration");
  }

  const handleUpdate = async () => {
    const toastOptions: ToastOptions = {
      closeOnClick: true,
      containerId: "z-index-9999",
    };

    try {
      await clientService.updateDefaultClientRoles(clientDefaultContext.defaultRoles);
      toast.info("Default client roles updated.", toastOptions);
      setRedirect("../client-administration");
    } catch {
      toast.error("An error occurred while updating default client roles.", toastOptions);
    }
  }

  if (!user || !user.roles.includes(Roles.QUEST_ADMIN)) {
    return <NotFound />;
  } else if (redirect) {
    return <Redirect to={redirect} />
  } else if (clientDefaultContext.loading) {
    return <div className="mt-3"><LoadingSpinner /></div>
  } else {
    return (
      <Page documentTitle="Client Default Roles">
        <Breadcrumbs template={BreadcrumbTemplateEnum.ClientAdministration} items={["Client Default Roles"]} />
        <PageInner>
          <PageHeader title={"Default Roles for New Clients"} disableHR={false} />
          <PageSection>
            <div className="col-12 mt-3 mb-3">
              <div className="row">
                The roles selected below will be the default role selections for new clients. No role can be granted to
                a user that is not already granted to their client. These selections may be overridden at the time clients
                are created, but these serve as a baseline to save you time in the client creation process.
              </div>
            </div>
          </PageSection>
          <PageSection>
            <div className="col-12">
              <QESMultiSelect
                containerClassName="mb-3"
                disabled={false}
                label="Default Roles"
                onChange={updateRoles}
                options={roleOptions}
                required={false}
                testId="client-default__roles" />
            </div>
          </PageSection>
        </PageInner>
        <WizardNavigationBar
          leftSide={
            <QESButton qesTestId="client-default__cancel" qesType="secondary" className="pull-left" onClick={handleCancel}>Cancel</QESButton>
          }
          rightSide={
            <QESButton qesTestId="client_default__save" qesType="primary" className="pull-right" disabled={!updateRequired} onClick={handleUpdate}>Update</QESButton>}
        />
      </Page >
    );
  }
}

export const ClientDefaultRoles: React.FC<React.PropsWithChildren<unknown>> = () => {
  return (
    <ClientDefaultProvider>
      <ClientDefaultRolesWithContext />
    </ClientDefaultProvider>
  );
}